/**
 * QR Code Modal Component
 * Displays a large QR code for reward redemption
 */

import { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  Tooltip,
  Divider,
  Alert,
} from '@mui/material'
import {
  Close,
  ContentCopy,
  Brightness7,
  BrightnessHigh,
  Warning,
} from '@mui/icons-material'
import { ThemedQRCode } from '@/components/ThemedQRCode'
import type { UserRewardDto, GiftRewardInstanceDto } from '@api/types/rewards'
import { RewardInstanceStatus } from '@api/types/rewards'
import { formatRemainingUses, formatExpirationDate } from '../utils/rewardHelpers'
import { useAuthStore } from '@store/authStore'

interface QRCodeModalProps {
  open: boolean
  onClose: () => void
  reward: UserRewardDto | GiftRewardInstanceDto | null
}

export function QRCodeModal({ open, onClose, reward }: QRCodeModalProps) {
  const [brightMode, setBrightMode] = useState(false)
  const [copied, setCopied] = useState(false)
  const { user } = useAuthStore()

  if (!reward) return null

  // Generate QR code data - uniqueCode already starts with R
  const uniqueCode = (reward as any).uniqueCode || ''
  const qrData = uniqueCode
  const isExpired = reward.status === RewardInstanceStatus.Expired

  const handleCopyCode = () => {
    navigator.clipboard.writeText(qrData)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          ...(brightMode && {
            filter: 'brightness(1.3)',
          }),
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h6">Reward QR Code</Typography>
        <Box>
          <Tooltip title={brightMode ? 'Normal brightness' : 'Increase brightness'}>
            <IconButton onClick={() => setBrightMode(!brightMode)} size="small" sx={{ mr: 1 }}>
              {brightMode ? <BrightnessHigh /> : <Brightness7 />}
            </IconButton>
          </Tooltip>
          <IconButton onClick={onClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Expired warning */}
        {isExpired && (
          <Alert severity="warning" icon={<Warning />} sx={{ mb: 2 }}>
            This reward has expired. The business may choose to honor it at their discretion.
          </Alert>
        )}

        {/* QR Code */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            py: 2,
            position: 'relative',
          }}
        >
          <ThemedQRCode data={qrData} size={280} />
          {isExpired && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                pointerEvents: 'none',
              }}
            >
              <Typography
                sx={{
                  color: 'error.main',
                  fontSize: 48,
                  fontWeight: 'bold',
                  opacity: 0.3,
                  transform: 'rotate(-15deg)',
                  textTransform: 'uppercase',
                }}
              >
                Expired
              </Typography>
            </Box>
          )}
        </Box>

        {/* Reward info */}
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Typography variant="h6" gutterBottom>
            {reward.title || 'Untitled Reward'}
          </Typography>

          {user?.displayName && (
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Owner: {user.displayName}
            </Typography>
          )}

          {reward.remainingUses !== null && (
            <Typography variant="body2" sx={{ mb: 1 }}>
              {formatRemainingUses(reward.remainingUses)}
            </Typography>
          )}

          <Typography variant="caption" color="text.secondary">
            {formatExpirationDate(reward.expiresAt)}
          </Typography>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Instructions */}
        <Typography
          variant="body2"
          color="text.secondary"
          textAlign="center"
          sx={{ mb: 2 }}
        >
          Present this QR code to redeem your reward
        </Typography>

        {/* Manual code entry fallback */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: 1.5,
            bgcolor: 'action.hover',
            borderRadius: 1,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Code:
          </Typography>
          <Typography
            variant="body2"
            sx={{ fontFamily: 'monospace', letterSpacing: 1 }}
          >
            {qrData}
          </Typography>
          <Tooltip title={copied ? 'Copied!' : 'Copy code'}>
            <IconButton size="small" onClick={handleCopyCode}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
